import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { FiSearch, FiDownload } from 'react-icons/fi';
import reportesAvanzadosService from '../services/reportesAvanzadosService';
import Loading from '../components/Loading';
import './ReporteViaticosPage.css';

const ReporteViaticosPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [estado, setEstado] = useState('');

  const cargar = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (fechaDesde) filters.fecha_desde = fechaDesde;
      if (fechaHasta) filters.fecha_hasta = fechaHasta;
      if (estado) filters.estado = estado;
      const res = await reportesAvanzadosService.getReporteViaticos(filters);
      setData(res.data);
    } catch { toast.error('Error al cargar reporte de viaticos'); }
    finally { setLoading(false); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v || 0);

  const exportarExcel = async () => {
    try {
      const XLSX = (await import('xlsx')).default;
      const solicitudes = data?.solicitudes || [];
      const ws = XLSX.utils.json_to_sheet(solicitudes.map(s => ({
        Solicitante: s.solicitante_nombre || s.solicitante, Motivo: s.motivo, Destino: s.destino,
        'Fecha Inicio': s.fecha_inicio, 'Fecha Fin': s.fecha_fin, Estado: s.estado,
        Estimado: s.monto_estimado, Entregado: s.monto_entregado, Rendido: s.monto_rendido,
        Diferencia: s.diferencia,
      })));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Viaticos');
      XLSX.writeFile(wb, `reporte_viaticos.xlsx`);
    } catch { toast.error('Error al exportar'); }
  };

  const solicitudes = data?.solicitudes || data?.results || [];
  const totales = data?.totales || {};
  const porSolicitante = data?.por_solicitante || [];

  const getEstadoBadge = (estado) => {
    const map = {
      borrador: 'badge-gray', pendiente: 'badge-yellow', aprobado: 'badge-green',
      rechazado: 'badge-red', entregado: 'badge-cyan', rendido: 'badge-blue', cerrado: 'badge-dark',
    };
    return map[estado] || 'badge-gray';
  };

  return (
    <div className="reporte-viaticos-page">
      <div className="page-header"><h1>Reporte de Viaticos</h1></div>

      <div className="selector-bar">
        <div className="form-group">
          <label>Desde</label>
          <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Hasta</label>
          <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Estado</label>
          <select value={estado} onChange={e => setEstado(e.target.value)}>
            <option value="">Todos</option>
            <option value="borrador">Borrador</option>
            <option value="pendiente">Pendiente</option>
            <option value="aprobado">Aprobado</option>
            <option value="entregado">Entregado</option>
            <option value="rendido">Rendido</option>
            <option value="cerrado">Cerrado</option>
          </select>
        </div>
        <button className="btn-primary" onClick={cargar}><FiSearch /> Consultar</button>
        {data && <button className="btn-export" onClick={exportarExcel}><FiDownload /> Excel</button>}
      </div>

      {loading && <Loading />}

      {!loading && data && (
        <>
          {/* Resumen */}
          <div className="resumen-cards">
            <div className="stat-card" style={{ borderLeftColor: '#3b82f6' }}>
              <div className="stat-label">Total Solicitudes</div>
              <div className="stat-value">{totales.cantidad || solicitudes.length}</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#f59e0b' }}>
              <div className="stat-label">Monto Estimado</div>
              <div className="stat-value">{formatCurrency(totales.estimado || solicitudes.reduce((s, v) => s + parseFloat(v.monto_estimado || 0), 0))}</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#0891b2' }}>
              <div className="stat-label">Monto Entregado</div>
              <div className="stat-value">{formatCurrency(totales.entregado || solicitudes.reduce((s, v) => s + parseFloat(v.monto_entregado || 0), 0))}</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#059669' }}>
              <div className="stat-label">Monto Rendido</div>
              <div className="stat-value">{formatCurrency(totales.rendido || solicitudes.reduce((s, v) => s + parseFloat(v.monto_rendido || 0), 0))}</div>
            </div>
          </div>

          {/* Resumen por solicitante */}
          {porSolicitante.length > 0 && (
            <div className="seccion">
              <h3>Resumen por Solicitante</h3>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Solicitante</th>
                      <th style={{ textAlign: 'right' }}>Solicitudes</th>
                      <th style={{ textAlign: 'right' }}>Estimado</th>
                      <th style={{ textAlign: 'right' }}>Entregado</th>
                      <th style={{ textAlign: 'right' }}>Rendido</th>
                      <th style={{ textAlign: 'right' }}>Diferencia</th>
                    </tr>
                  </thead>
                  <tbody>
                    {porSolicitante.map((s, i) => {
                      const diff = parseFloat(s.diferencia || 0);
                      return (
                        <tr key={i}>
                          <td><strong>{s.solicitante_nombre || s.solicitante}</strong></td>
                          <td className="right">{s.cantidad}</td>
                          <td className="mono right">{formatCurrency(s.estimado)}</td>
                          <td className="mono right">{formatCurrency(s.entregado)}</td>
                          <td className="mono right">{formatCurrency(s.rendido)}</td>
                          <td className="mono right bold" style={{ color: diff >= 0 ? '#059669' : '#dc2626' }}>{formatCurrency(s.diferencia)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Detalle */}
          <div className="seccion">
            <h3>Detalle de Solicitudes</h3>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Solicitante</th>
                    <th>Motivo</th>
                    <th>Destino</th>
                    <th>Fechas</th>
                    <th style={{ textAlign: 'right' }}>Estimado</th>
                    <th style={{ textAlign: 'right' }}>Entregado</th>
                    <th style={{ textAlign: 'right' }}>Rendido</th>
                    <th style={{ textAlign: 'right' }}>Diferencia</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {solicitudes.length === 0 ? (
                    <tr><td colSpan="9" style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Sin viaticos para el periodo</td></tr>
                  ) : solicitudes.map((s, i) => {
                    const diff = parseFloat(s.diferencia || ((s.monto_entregado || 0) - (s.monto_rendido || 0)));
                    return (
                      <tr key={i}>
                        <td>{s.solicitante_nombre || s.solicitante || '-'}</td>
                        <td>{s.motivo}</td>
                        <td>{s.destino}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{s.fecha_inicio} - {s.fecha_fin}</td>
                        <td className="mono right">{formatCurrency(s.monto_estimado)}</td>
                        <td className="mono right">{formatCurrency(s.monto_entregado)}</td>
                        <td className="mono right">{formatCurrency(s.monto_rendido)}</td>
                        <td className="mono right bold" style={{ color: diff >= 0 ? '#059669' : '#dc2626' }}>{formatCurrency(diff)}</td>
                        <td><span className={`badge ${getEstadoBadge(s.estado)}`}>{s.estado_display || s.estado}</span></td>
                      </tr>
                    );
                  })}
                </tbody>
                {solicitudes.length > 0 && (
                  <tfoot>
                    <tr>
                      <td colSpan="4" style={{ fontWeight: 700 }}>TOTALES ({solicitudes.length})</td>
                      <td className="mono right bold">{formatCurrency(totales.estimado || solicitudes.reduce((s, v) => s + parseFloat(v.monto_estimado || 0), 0))}</td>
                      <td className="mono right bold">{formatCurrency(totales.entregado || solicitudes.reduce((s, v) => s + parseFloat(v.monto_entregado || 0), 0))}</td>
                      <td className="mono right bold">{formatCurrency(totales.rendido || solicitudes.reduce((s, v) => s + parseFloat(v.monto_rendido || 0), 0))}</td>
                      <td className="mono right bold">{formatCurrency((totales.entregado || 0) - (totales.rendido || 0))}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReporteViaticosPage;
